import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthenticationService } from './authentication.service';
import { StorageService } from '../../core/storage/storage.service';
import { User } from '../model/user';
import * as AuthActions from '../../stores/auth/actions/auth.actions';
import * as AuthApiActions from '../../stores/auth/actions/auth-api.actions';
import * as SigninPageActions from '../../stores/auth/actions/signin-page.actions';

@Injectable({
  providedIn: 'root'
})
export class TokenRefreshTimerService implements OnDestroy {

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly REFRESH_BUFFER_MS = 60_000; // 1 minute before expiry
  private readonly MIN_DELAY_MS = 10_000; // minimum 10 seconds

  constructor(
    private store: Store,
    private authenticationService: AuthenticationService,
    private storage: StorageService
  ) {}

  startTimer(expirationTokenDate: string): void {
    this.stopTimer();

    const expiryTime = new Date(expirationTokenDate).getTime();
    if (Number.isNaN(expiryTime)) {
      return;
    }
    const now = Date.now();
    const delay = Math.max(expiryTime - now - this.REFRESH_BUFFER_MS, this.MIN_DELAY_MS);

    this.refreshTimer = setTimeout(() => {
      this.performRefresh();
    }, delay);
  }

  stopTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  private performRefresh(): void {
    const currentUser: User = this.storage.get('currentUser');
    if (!currentUser) {
      this.stopTimer();
      return;
    }

    this.authenticationService.refreshOnce(currentUser.accessToken).subscribe({
      next: (response) => {
        if (response && response.data && response.data.accessToken) {
          const refreshedUser = {
            accessToken: response.data.accessToken,
            expirationTokenDate: response.data.expirationTokenDate,
            language: currentUser.language,
            ...(currentUser.username ? { username: currentUser.username } : {}),
          };

          this.storage.set('currentUser', JSON.stringify(refreshedUser));
          this.store.dispatch(AuthActions.updateToken({
            token: response.data.accessToken
          }));
        }
      },
      error: (error) => {
        if (error?.status === 401 || error?.status === 403) {
          this.store.dispatch(SigninPageActions.autoLogout());
          this.store.dispatch(AuthApiActions.loginRedirect({ url: '' }));
        }
      }
    });
  }
}
